import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';

export function SignupPage() {
  const navigate = useNavigate();
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await signUpWithEmail(email, password);
      navigate(ROUTES.VERIFY_EMAIL);
    } catch (err) {
      setError('Failed to create account. Email may already be in use.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignUp() {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();
      navigate(ROUTES.ONBOARDING);
    } catch (err) {
      setError('Failed to sign up with Google');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center py-10 bg-cacao-dark">
      <div className="w-full max-w-sm px-4">
        <h1 className="text-center font-heading text-lg font-medium text-[#F5F5F5]">Start your free trial</h1>
        <p className="mt-1 text-center text-xs text-cacao-text-tertiary">14 days free. No credit card required.</p>

        {error && (
          <div className="mt-3 rounded-md bg-red-500/20 p-2.5 text-xs text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-3">
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-cacao-muted">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-medium text-cacao-muted">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-xs font-medium text-cacao-muted">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-cacao-dark-tertiary" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-cacao-dark px-3 text-cacao-text-tertiary">or</span>
          </div>
        </div>

        <button
          onClick={handleGoogleSignUp}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-secondary px-3 py-2 text-sm font-medium text-[#F5F5F5] hover:bg-cacao-dark-tertiary disabled:opacity-50 transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <p className="mt-6 text-center text-xs text-cacao-text-tertiary">
          Already have an account?{' '}
          <Link to={ROUTES.LOGIN} className="font-medium text-cacao-muted hover:text-white transition-colors">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
